// @ts-nocheck
/**
 * Tencent is pleased to support the open source community by making CherryMarkdown available.
 *
 * Copyright (C) 2021 Tencent. All rights reserved.
 * The below software in this distribution may have been modified by Tencent ("Tencent Modifications").
 *
 * All Tencent Modifications are Copyright (C) Tencent.
 *
 * CherryMarkdown is licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import escapeRegExp from 'lodash/escapeRegExp';
import SyntaxBase from '@/core/SyntaxBase';
import { allSuggestList, suggesterKeywords } from '@/core/hooks/SuggestList';
import { isLookbehindSupported } from '@/utils/regexp';
import { replaceLookbehind } from '@/utils/lookbehind-replace';
import { isBrowser } from '@/utils/env';

/**
 * @typedef {import('@codemirror/view').EditorView} EditorView
 * @typedef {import('~types/editor').CM6Adapter} CM6Adapter
 */

/**
 * @typedef { Object } SuggestListItemObject 推荐列表项对象
 * @property { string } icon 图标
 * @property { string } label 候选列表回显的内容
 * @property { string } value 点击候选项的时候回填的值
 * @property { string } keyword 关键词，通过关键词控制候选项的显隐
 * @typedef { SuggestListItemObject | string } SuggestListItem 推荐列表项
 * @typedef { Array<SuggestListItem> } SuggestList 推荐列表
 */

/**
 * @typedef {object} SuggesterConfigItem
 * @property {function(string, function(SuggestList): void): void} suggestList - 推荐项来源函数，接收搜索关键字和回调函数
 * @property {string} keyword - 唤醒关键字（如 '@'、'#' 等）
 * @property {function} suggestListRender - 自定义渲染函数（可选）
 * @property {function} echo - 回填回调函数（可选）
 * @typedef {object} SuggesterConfig
 * @property {Array<SuggesterConfigItem>} suggester - 推荐器配置数组
 */

export default class Suggester extends SyntaxBase {
  static HOOK_NAME = 'suggester';

  constructor({ config, cherry }) {
    /**
     * config.suggester 推荐器配置数组，每项配置：
     * - suggestList: 推荐项来源（函数或字符串），函数接收关键字和结果回调
     * - keyword: 唤醒关键字（如 '@'、'#' 等）
     * - suggestListRender: 自定义渲染函数（可选）
     * - echo: 回填回调函数（可选）
     */
    super({ needCache: true });

    this.config = config;
    this.$cherry = cherry;
    this.suggesterPanel = new SuggesterPanel(cherry);

    if (!this.inited) {
      this.initConfig(this.config);
    }

    this.RULE = this.rule();
  }

  afterInit(callback) {
    // node环境下直接跳过输入联想
    if (!isBrowser()) {
      return;
    }
    if (typeof callback === 'function') {
      callback();
    }
  }

  /**
   * 初始化配置
   * @param {SuggesterConfig} config
   */
  initConfig(config) {
    let { suggester } = this.$cherry.options.editor.suggester || {};

    this.suggester = {};
    const defaultSuggest = [];
    const that = this;
    // 默认的唤醒关键字配置
    for (const suggesterKeyword of suggesterKeywords) {
      defaultSuggest.push({
        keyword: suggesterKeyword,
        suggestList(_word, callback) {
          const word = _word.toLowerCase();
          const systemSuggestList = allSuggestList(
            suggesterKeyword,
            that.$cherry.locale,
            that.$cherry.options.editor.suggester,
          );
          // 加个空格就直接退出联想
          if (/^\s$/.test(word)) {
            callback(false);
            return;
          }
          const keyword = word
            .replace(/\s+/g, '')
            .replace(new RegExp(`^${suggesterKeyword}`, 'g'), '')
            .replace(/^[#]+/, '#')
            .replace(/^[/]+/, '/')
            .split('')
            .join('.*?');
          const test = new RegExp(`^.*?${keyword}.*?$`, 'i');
          const suggestList = systemSuggestList.filter((item) => {
            if (item.exactMatch) {
              return !word || item.keyword === word;
            }
            return !word || test.test(item.keyword);
          });
          callback(suggestList.length === 0 ? false : suggestList);
        },
        echo() {
          return '';
        },
      });
    }
    if (!suggester) {
      suggester = defaultSuggest;
    } else {
      suggester = defaultSuggest.concat(suggester);
    }

    suggester.forEach((configItem) => {
      if (!configItem.suggestList) {
        console.warn('[cherry-suggester]: the suggestList of config is missing.');
        return;
      }

      if (!configItem.keyword) {
        configItem.keyword = '@';
      }
      this.suggester[configItem.keyword] = configItem;
    });

    // 反复初始化时，缓存还在但 DOM 已更新的情况
    if (this.suggesterPanel.hasEditor()) {
      this.suggesterPanel.editor = null;
    }

    this.inited = true;
  }

  makeHtml(str) {
    if (!this.RULE.reg) return str;
    if (!this.suggesterPanel.hasEditor() && isBrowser()) {
      const { editor } = this.$engine.$cherry;
      this.suggesterPanel.setEditor(editor);
      this.suggesterPanel.setSuggester(this.suggester);
      this.suggesterPanel.bindEvent();
    }
    if (isLookbehindSupported()) {
      return str.replace(this.RULE.reg, this.toHtml.bind(this));
    }
    return replaceLookbehind(str, this.RULE.reg, this.toHtml.bind(this), true, 1);
  }

  toHtml(wholeMatch, leadingChar, keyword, text) {
    if (text) {
      return (
        this.suggester[keyword]?.echo?.call(this, text) ||
        `${leadingChar}<span class="cherry-suggestion">${keyword}${text}</span>`
      );
    }
    if (this.suggester[keyword]?.echo === false) {
      return `${leadingChar}`;
    }
    if (!this.suggester[keyword]) {
      return leadingChar + text;
    }
    return text ? leadingChar + text : `${leadingChar}`;
  }

  rule() {
    if (!this.config?.suggester || Object.keys(this.config?.suggester).length <= 0) {
      return {};
    }

    let suggester;
    if (Array.isArray(this.config.suggester)) {
      suggester = this.config.suggester.map((obj) => obj.keyword || '');
    } else {
      suggester = Object.keys(this.config.suggester).map((key) => this.config.suggester[key].keyword || '');
    }

    const keys = suggester.map((key) => escapeRegExp(key)).join('|');
    const reg = new RegExp(
      `${isLookbehindSupported() ? '((?<!\\\\))[ ]' : '(^|[^\\\\])[ ]'}(${keys})(([^${keys}\\s])+)`,
      'g',
    );
    return /** @type {any} */ ({
      reg,
    });
  }

  mounted() {
    if (!this.suggesterPanel.hasEditor() && isBrowser()) {
      const { editor } = this.$engine.$cherry;
      this.suggesterPanel.setEditor(editor);
      this.suggesterPanel.setSuggester(this.suggester);
      this.suggesterPanel.bindEvent();
    }
  }
}

class SuggesterPanel {
  constructor(cherry) {
    this.searchCache = false;
    this.searchKeyCache = [];
    this.optionList = [];
    this.cursorMove = true;
    this.suggesterConfig = {};
    this.$cherry = cherry;
    this.panelPosition = 'below'; // 面板相对光标的显示位置（上方或下方）：'below' | 'above'
  }

  /**
   * 如果没有panel，则尝试初始化一个，在node模式不初始化
   */
  tryCreatePanel() {
    if (!this.$suggesterPanel && isBrowser() && document) {
      this.$cherry.wrapperDom.appendChild(this.createDom(this.panelWrap));
      this.$suggesterPanel = this.$cherry.wrapperDom.querySelector('.cherry-suggester-panel');
    }
  }

  panelWrap = `<div class="cherry-suggester-panel"></div>`;

  hasEditor() {
    // CodeMirror 6: 使用 view 属性检查编辑器是否存在
    return !!this.editor && !!this.editor.editor && !!this.editor.editor.view;
  }

  /**
   * 设置编辑器
   * @param {import('@/Editor').default} editor
   */
  setEditor(editor) {
    this.editor = editor;
  }

  setSuggester(suggester) {
    this.suggesterConfig = suggester;
  }

  bindEvent() {
    if (!this.editor?.options?.showSuggestList) {
      return;
    }
    let keyAction = false;

    // change 事件必须使用 editor.editor.on('change')，因为 onCodeMirrorChange
    // 需要 {text, from, to, origin} 格式的变更细节，而 $cherry.$event 的 afterChange
    // 只传递 {markdownText, html}，不含这些信息
    this.boundOnChange = (codemirror, evt) => {
      keyAction = true;
      this.onCodeMirrorChange(codemirror, evt);
    };
    this.editor.editor.on('change', this.boundOnChange);

    // cursorActivity 和 scroll 使用 $cherry.$event 模式
    this.boundOnCursorActivity = () => {
      if (!keyAction) this.stopRelate();
      keyAction = false;
    };
    this.boundOnScroll = () => {
      if (!this.searchCache) {
        return;
      }
      this.relocatePanel(this.editor.editor);
    };

    this.$cherry.$event.on('beforeSelectionChange', this.boundOnCursorActivity);
    this.$cherry.$event.on('onScroll', this.boundOnScroll);

    // CM6: 注册方向键拦截器（替代 CM5 的 extraKeys）
    const keyCodeMap = { ArrowUp: 38, ArrowDown: 40, Enter: 13, Escape: 27 };
    this.editor.arrowKeyInterceptor = (key) => {
      keyAction = true;
      if (!this.enableRelate()) return false;
      // Enter 键特殊处理：直接执行选择，避免 onKeyDown 中 replaceRange 触发 stopRelate 导致状态混乱
      if (key === 'Enter') {
        const index = this.findSelectedItemIndex();
        if (index >= 0) {
          // 有选中项：执行选择并关闭面板
          this.pasteSelectResult(index);
          this.editor.editor.view.focus();
          setTimeout(() => this.stopRelate(), 0);
          return true;
        }
        // 无选中项但面板打开：关闭面板，阻止默认换行
        // 这样用户可以通过按 Enter 快速关闭面板
        setTimeout(() => this.stopRelate(), 0);
        return true;
      }
      this.onKeyDown(this.editor.editor, { keyCode: keyCodeMap[key], key, preventDefault() {}, stopPropagation() {} });
      return !this.cursorMove;
    };

    this.onClickPanelItem();
  }

  onClickPanelItem() {
    this.tryCreatePanel();
    // 保存事件处理器引用，便于 destroy 时移除
    this.boundClickHandler = (evt) => {
      const idx = this.findClickedItemIndex(evt.target);
      if (idx > -1) {
        this.editor.editor.view.focus();
        this.pasteSelectResult(idx);
      }
      // 使用 setTimeout 延迟 stopRelate，与 Enter 键处理保持一致
      // 避免 replaceRange 触发 change 事件期间状态被清空
      setTimeout(() => this.stopRelate(), 0);
    };
    this.$suggesterPanel.addEventListener('click', this.boundClickHandler, false);
  }

  /**
   * 查找被点击元素对应的选项索引
   * 支持点击 item 内部的子元素（如图标）
   * @param {HTMLElement} target 被点击的元素
   * @returns {number} 选项索引，未找到返回 -1
   */
  findClickedItemIndex(target) {
    if (!this.$suggesterPanel) return -1;
    let node = target;
    // 向上查找最近的 item 元素
    while (node && node !== this.$suggesterPanel) {
      if (node.classList && node.classList.contains('cherry-suggester-panel__item')) {
        // 找到 item，计算其索引
        return Array.prototype.indexOf.call(this.$suggesterPanel.children, node);
      }
      node = node.parentElement;
    }
    return -1;
  }

  showSuggesterPanel({ left, top, items }) {
    this.tryCreatePanel();
    if (!this.$suggesterPanel && isBrowser()) {
      this.$cherry.wrapperDom.appendChild(this.createDom(this.panelWrap));
      this.$suggesterPanel = this.$cherry.wrapperDom.querySelector('.cherry-suggester-panel');
    }

    this.updatePanel(items);
    this.$suggesterPanel.style.left = `${left}px`;
    this.$suggesterPanel.style.top = `${top}px`;
    this.$suggesterPanel.style.display = 'block';
    this.$suggesterPanel.style.position = 'absolute';
    this.$suggesterPanel.style.zIndex = '100';
  }

  hideSuggesterPanel() {
    this.tryCreatePanel();
    // const $suggesterPanel = document.querySelector('.cherry-suggester-panel');
    if (this.$suggesterPanel) {
      this.$suggesterPanel.style.display = 'none';
    }
  }

  /**
   * 更新suggesterPanel
   * @param {SuggestList} suggestList
   */
  updatePanel(suggestList) {
    this.tryCreatePanel();
    let defaultValue = suggestList
      .map((suggest, idx) => {
        if (typeof suggest === 'object' && suggest !== null) {
          let renderContent = suggest.label;
          if (suggest?.icon) {
            renderContent = `<i class="ch-icon ch-icon-${suggest.icon}"></i>${renderContent}`;
          }
          return this.renderPanelItem(renderContent, false);
        }
        return this.renderPanelItem(suggest, false);
      })
      .join('');
    /**
     * @type { SuggesterConfigItem }
     */
    const suggesterConfig = this.suggesterConfig[this.keyword];
    // 用户自定义渲染逻辑 suggestListRender
    if (suggesterConfig && typeof suggesterConfig.suggestListRender === 'function') {
      defaultValue = suggesterConfig.suggestListRender.call(this, suggestList) || defaultValue;
    }

    this.$suggesterPanel.innerHTML = ''; // 清空
    if (typeof defaultValue === 'string') {
      this.$suggesterPanel.innerHTML = defaultValue;
    } else if (Array.isArray(defaultValue) && defaultValue.length > 0) {
      defaultValue.forEach((item) => {
        this.$suggesterPanel.appendChild(item);
      });
    } else if (typeof defaultValue === 'object' && defaultValue.nodeType === 1) {
      this.$suggesterPanel.appendChild(defaultValue);
    }
  }

  /**
   * 渲染suggesterPanel item
   * @param {string} item 渲染内容
   * @param {boolean} selected 是否选中
   * @returns {string} html
   */
  renderPanelItem(item, selected) {
    if (selected) {
      return `<div class="cherry-suggester-panel__item cherry-suggester-panel__item--selected">${item}</div>`;
    }
    return `<div class="cherry-suggester-panel__item">${item}</div>`;
  }

  createDom(string = '') {
    if (!this.template) {
      this.template = document.createElement('div');
    }

    this.template.innerHTML = string.trim();

    // Change this to div.childNodes to support multiple top-level nodes
    const frag = document.createDocumentFragment();
    Array.prototype.map.call(this.template.childNodes, (item, idx) => {
      frag.appendChild(item);
    });
    return frag;
  }

  /**
   * 面板重定位（滚动时调用，不进行边界判定）
   * @param {CodeMirror} codemirror
   */
  relocatePanel(codemirror) {
    // CodeMirror 6: 使用 coordsAtPos 获取光标位置
    const editorView = this.editor?.editor?.view;
    if (!editorView) {
      return false;
    }

    const cursorPos = editorView.state.selection.main.head;
    const cursorCoords = editorView.coordsAtPos(cursorPos);

    if (!cursorCoords) {
      return false;
    }

    const editorDomRect = this.$cherry.wrapperDom.getBoundingClientRect();

    const left = cursorCoords.left - editorDomRect.left;
    const cursorTop = cursorCoords.top - editorDomRect.top;
    const cursorHeight = cursorCoords.bottom - cursorCoords.top;

    let top;

    // 根据之前记录的位置关系计算新位置
    if (this.panelPosition === 'below') {
      top = cursorTop + cursorHeight + 5;
    } else {
      // 需要获取面板当前高度
      const panelHeight = this.$suggesterPanel ? this.$suggesterPanel.offsetHeight : 380;
      top = cursorTop - panelHeight - 5;
    }

    this.showSuggesterPanel({ left, top, items: this.optionList });
  }

  /**
   * 获取光标位置
   * @param {CodeMirror} codemirror
   * @returns {{ left: number, top: number }}
   */
  getCursorPos(codemirror) {
    // CodeMirror 6: 使用 coordsAtPos 获取光标位置
    const editorView = this.editor?.editor?.view;
    if (!editorView) return null;

    const cursorPos = editorView.state.selection.main.head;
    const cursorCoords = editorView.coordsAtPos(cursorPos);

    if (!cursorCoords) return null;

    const lineHeight = cursorCoords.bottom - cursorCoords.top;
    const top = cursorCoords.top + lineHeight;
    const { left } = cursorCoords;
    return { left, top };
  }

  // 开启关联
  startRelate(codemirror, keyword, from) {
    this.cursorFrom = from;
    this.keyword = keyword;
    this.searchCache = true;

    // 在下一帧进行首次定位和边界判定
    requestAnimationFrame(() => {
      this.relocatePanelWithBoundaryCheck();
    });
  }

  /**
   * 首次显示面板时进行边界判定（在下一帧执行）
   */
  relocatePanelWithBoundaryCheck() {
    // 先创建并显示面板以获取实际高度（默认是 380px）
    this.tryCreatePanel();
    this.updatePanel(this.optionList);

    this.$suggesterPanel.style.visibility = 'hidden';
    this.$suggesterPanel.style.display = 'block';
    this.$suggesterPanel.style.position = 'absolute';

    const actualPanelHeight = this.$suggesterPanel.offsetHeight || 380;

    // CodeMirror 6: 光标在编辑器 view.dom 内部
    // 需要从编辑器实例获取光标位置，而不是通过 DOM 查询
    const editorView = this.editor?.editor?.view;
    if (!editorView) {
      this.$suggesterPanel.style.display = 'none';
      return false;
    }

    // 使用 CM6 的 coordsAtPos 获取光标位置
    const cursorPos = editorView.state.selection.main.head;
    const cursorCoords = editorView.coordsAtPos(cursorPos);

    if (!cursorCoords) {
      this.$suggesterPanel.style.display = 'none';
      return false;
    }

    const editorDomRect = this.$cherry.wrapperDom.getBoundingClientRect();

    // 计算位置 - 使用 CM6 的 coordsAtPos 返回的坐标
    const left = cursorCoords.left - editorDomRect.left;
    const cursorTop = cursorCoords.top - editorDomRect.top;
    const cursorHeight = cursorCoords.bottom - cursorCoords.top;
    const cursorBottomInView = cursorTop + cursorHeight;

    // 获取可用空间
    const editorHeight = this.$cherry.wrapperDom.clientHeight;
    const spaceBelow = editorHeight - cursorBottomInView;
    const spaceAbove = cursorTop;

    // 边界判定
    let top;

    if (spaceBelow >= actualPanelHeight + 10) {
      // 下方空间足够
      top = cursorBottomInView + 5;
      this.panelPosition = 'below';
    } else if (spaceAbove >= actualPanelHeight + 10) {
      // 上方空间足够
      top = cursorTop - actualPanelHeight - 5;
      this.panelPosition = 'above';
    } else {
      // 两边都不够，选择空间较大的一边
      if (spaceBelow > spaceAbove) {
        top = cursorBottomInView + 5;
        this.panelPosition = 'below';
      } else {
        top = Math.max(10, cursorTop - actualPanelHeight - 5);
        this.panelPosition = 'above';
      }
    }

    // 设置最终位置并显示
    this.$suggesterPanel.style.left = `${left}px`;
    this.$suggesterPanel.style.top = `${top}px`;
    this.$suggesterPanel.style.visibility = 'visible';
    this.$suggesterPanel.style.zIndex = '100';
  }

  // 关闭关联
  stopRelate() {
    this.hideSuggesterPanel();
    this.cursorFrom = null;
    this.cursorTo = null;
    this.keyword = '';
    this.searchKeyCache = [];
    this.searchCache = false;
    this.cursorMove = true;
    this.optionList = [];
    this.panelPosition = 'below'; // 重置面板位置记录
  }

  /**
   * 粘贴选择结果
   * @param {number} idx 选择的结果索引
   * @param {KeyboardEvent} [evt] 键盘事件（可选）
   */
  pasteSelectResult(idx, evt) {
    const { cursorFrom } = this;
    if (cursorFrom === null || cursorFrom === undefined) {
      return;
    }
    const optionItem = this.optionList[idx];
    if (!optionItem) {
      return;
    }

    const docLength = this.editor?.editor?.state?.doc?.length;

    // 验证 cursorFrom 是否仍然有效
    if (typeof docLength === 'number' && cursorFrom > docLength) {
      this.stopRelate();
      return;
    }

    let cursorTo;
    // 仅替换当前联想触发期间录入的字符 (issue #1493)
    const typedLength = Array.isArray(this.searchKeyCache) ? this.searchKeyCache.join('').length : 0;
    if (typedLength > 0) {
      cursorTo = cursorFrom + typedLength;
    } else if (this.cursorTo !== null && this.cursorTo !== undefined) {
      cursorTo = this.cursorTo;
    } else {
      cursorTo = cursorFrom;
    }

    if (typeof docLength === 'number') {
      cursorTo = Math.min(cursorTo, docLength);
    }

    if (optionItem) {
      let result = '';
      if (typeof optionItem === 'object' && optionItem !== null && typeof optionItem.value === 'string') {
        result = optionItem.value;
      } else if (typeof optionItem === 'object' && optionItem !== null && typeof optionItem.value === 'function') {
        result = optionItem.value();
      } else {
        result = ` ${this.keyword}${optionItem} `;
      }
      // 如果回填内容以空格结尾，同时光标位置后还有空格，则一并替换
      if (result.endsWith(' ') && this.editor?.editor?.state && cursorTo !== null) {
        const { doc } = this.editor.editor.state;
        const currentDocLength = doc?.length;
        if (typeof currentDocLength === 'number' && cursorTo < currentDocLength) {
          const charAtCursor = doc.sliceString(cursorTo, cursorTo + 1);
          if (charAtCursor === ' ') {
            cursorTo = cursorTo + 1;
          }
        }
      }
      // CM6: replaceRange 使用文档偏移量
      if (result) {
        this.editor.editor.replaceRange(result, cursorFrom, cursorTo);
      }
      // 控制光标左移若干位
      // CM6: getCursor() 返回文档偏移量
      if (optionItem.goLeft) {
        const cursor = this.editor.editor.view.state.selection.main.head;
        this.editor.editor.view.dispatch({ selection: { anchor: cursor - optionItem.goLeft } });
      }
      // 控制光标上移若干位
      // CM6: 需要将偏移量转换为行，然后向上移动
      if (optionItem.goTop) {
        const cursor = this.editor.editor.view.state.selection.main.head;
        const { doc } = this.editor.editor.state;
        const currentLine = doc.lineAt(cursor);
        const targetLineNumber = Math.max(1, currentLine.number - optionItem.goTop);
        const targetLine = doc.line(targetLineNumber);
        const ch = cursor - currentLine.from;
        const newPos = targetLine.from + Math.min(ch, targetLine.length);
        this.editor.editor.view.dispatch({ selection: { anchor: newPos } });
      }
      // 选中某个范围
      // CM6: setSelection 使用文档偏移量
      if (optionItem.selection) {
        const cursor = this.editor.editor.view.state.selection.main.head;
        const fromPos = cursor - optionItem.selection.from;
        const toPos = cursor - optionItem.selection.to;
        this.editor.editor.setSelection(fromPos, toPos);
      }
    }
  }

  /**
   * 寻找当前选中项的索引
   * @returns {number} 选中项索引，未找到或面板不存在返回 -1
   */
  findSelectedItemIndex() {
    if (!this.$suggesterPanel) return -1;
    return Array.prototype.findIndex.call(
      this.$suggesterPanel.children,
      (item) => item.classList && item.classList.contains('cherry-suggester-panel__item--selected'),
    );
  }

  enableRelate() {
    return this.searchCache;
  }

  /**
   *  codeMirror change事件
   * @param {CodeMirror.Editor} codemirror
   * @param {CodeMirror.EditorChange} evt
   * @returns
   */
  onCodeMirrorChange(codemirror, evt) {
    const { text, from, to, origin } = evt;
    const changeValue = text.length === 1 ? text[0] : '';

    // 首次输入命中关键词的时候开启联想
    if (!this.enableRelate() && this.suggesterConfig[changeValue]) {
      this.startRelate(codemirror, changeValue, from);
    }
    if (this.enableRelate() && (changeValue || origin === '+delete')) {
      this.cursorTo = to;
      if (changeValue) {
        this.searchKeyCache.push(changeValue);
      } else if (origin === '+delete') {
        this.searchKeyCache.pop();
        if (this.searchKeyCache.length === 0) {
          this.stopRelate();
          return;
        }
      }
      // 展示推荐列表
      if (typeof this.suggesterConfig[this.keyword]?.suggestList === 'function') {
        // 请求api 返回结果拼凑
        this.suggesterConfig[this.keyword].suggestList(this.searchKeyCache.join(''), (res) => {
          // 如果返回了false，则强制退出联想
          if (res === false) {
            this.stopRelate();
            return;
          }
          // 回显命中的结果
          this.optionList = !res || !res.length ? [] : res;
          this.updatePanel(this.optionList);
        });
      }
    }
  }

  /**
   * 监听方向键选择 options
   * @param {import('@codemirror/view').EditorView} codemirror
   * @param {KeyboardEvent} evt
   */
  onKeyDown(codemirror, evt) {
    this.tryCreatePanel();
    if (!this.$suggesterPanel) {
      return false;
    }
    const { keyCode } = evt;
    // up down
    if ([38, 40].includes(keyCode)) {
      // 阻止事件传播到 CodeMirror，避免光标移动
      evt.preventDefault();
      evt.stopPropagation();

      // issue 558
      if (this.optionList.length === 0) {
        setTimeout(() => {
          this.stopRelate();
        }, 0);
        return;
      }

      this.cursorMove = false;

      // 初始状态下
      // - 按上键（38）选择最后一项
      // - 按下键（40）选择第一项
      const existingSelected = this.$suggesterPanel.querySelector('.cherry-suggester-panel__item--selected');
      const hasSelected = !!existingSelected;
      const selectedItem = existingSelected || null;
      let nextElement = null;

      if (!hasSelected) {
        if (keyCode === 38) {
          nextElement = this.$suggesterPanel.lastElementChild;
        } else if (keyCode === 40) {
          nextElement = this.$suggesterPanel.firstElementChild;
        }
      } else {
        if (keyCode === 38 && !selectedItem.previousElementSibling) {
          nextElement = this.$suggesterPanel.lastElementChild;
          // codemirror.focus();
        } else if (keyCode === 40 && !selectedItem.nextElementSibling) {
          nextElement = this.$suggesterPanel.firstElementChild;
          // codemirror.focus();
        } else {
          if (keyCode === 38) {
            nextElement = selectedItem.previousElementSibling;
          } else if (keyCode === 40) {
            nextElement = selectedItem.nextElementSibling;
          }
        }

        // 已选中情况下 移除当前选中样式
        if (selectedItem && selectedItem.classList) {
          selectedItem.classList.remove('cherry-suggester-panel__item--selected');
        }
      }

      // 如果没有可选元素则退出
      if (!nextElement) {
        setTimeout(() => {
          this.stopRelate();
        }, 0);
        return;
      }

      nextElement.classList.add('cherry-suggester-panel__item--selected');

      // 提示面板高度
      const suggestPanelHeight = this.$suggesterPanel.offsetHeight;
      // 可视区域范围上端
      const viewTop = this.$suggesterPanel.scrollTop;
      // 可视区域范围下端
      const viewBottom = viewTop + suggestPanelHeight;
      // item的上端
      const nextEleTop = nextElement.offsetTop;
      // item高度
      const nextEleHeight = nextElement.offsetHeight;
      // 当前元素全部或部分在可视区域之外，就滚动
      if (nextEleTop < viewTop || nextEleTop + nextEleHeight > viewBottom) {
        this.$suggesterPanel.scrollTop = nextEleTop - suggestPanelHeight / 2;
      }
    } else if (keyCode === 13) {
      const index = this.findSelectedItemIndex();
      if (index >= 0) {
        // 阻止事件传播，避免插入换行
        evt.preventDefault();
        evt.stopPropagation();
        this.cursorMove = false;
        this.pasteSelectResult(index, evt);
        codemirror.view.focus();
      }
      setTimeout(() => {
        this.stopRelate();
      }, 0);
    } else if (keyCode === 27 || keyCode === 0x25 || keyCode === 0x27) {
      // 按下esc或者←、→键的时候退出联想
      evt.preventDefault();
      evt.stopPropagation();
      codemirror.view.focus();
      setTimeout(() => {
        this.stopRelate();
      }, 0);
    }
  }

  /**
   * 销毁 SuggesterPanel 实例，清理事件监听器和 DOM 引用
   * 必须调用此方法以避免内存泄漏
   */
  destroy() {
    // 移除 editor.editor 上的 change 事件监听器
    if (this.editor && this.editor.editor && this.boundOnChange) {
      this.editor.editor.off('change', this.boundOnChange);
    }
    // 移除 $cherry.$event 上的事件监听器
    if (this.$cherry && this.$cherry.$event) {
      if (this.boundOnCursorActivity) {
        this.$cherry.$event.off('beforeSelectionChange', this.boundOnCursorActivity);
      }
      if (this.boundOnScroll) {
        this.$cherry.$event.off('onScroll', this.boundOnScroll);
      }
    }
    this.boundOnChange = null;
    this.boundOnCursorActivity = null;
    this.boundOnScroll = null;

    // 移除 panel 上的 click 事件监听器
    if (this.$suggesterPanel && this.boundClickHandler) {
      this.$suggesterPanel.removeEventListener('click', this.boundClickHandler, false);
    }
    // 清理 DOM 引用
    if (this.$suggesterPanel && this.$suggesterPanel.parentNode) {
      this.$suggesterPanel.parentNode.removeChild(this.$suggesterPanel);
    }
    this.$suggesterPanel = null;
    this.editor = null;
    this.$cherry = null;
    this.suggesterConfig = {};
    this.optionList = [];
    this.searchKeyCache = [];
  }
}
